import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { subscribeToPush } from '../../lib/pushNotifications';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import NotificationCard from './components/NotificationCard';

const NotificationsReceived = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [pushStatus, setPushStatus] = useState('unknown');

  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterDest, setFilterDest] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    supabase?.auth?.getUser()?.then(({ data }) => setUserEmail(data?.user?.email || ''));
    if ('Notification' in window) setPushStatus(Notification.permission);
  }, []);

  const handleEnablePush = async () => {
    setPushStatus('subscribing');
    const success = await subscribeToPush();
    setPushStatus(success ? 'granted' : 'denied');
  };

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      ?.from('notifications')
      ?.select('*, users!target_manager_id(name, email)')
      ?.order('created_at', { ascending: false });
    setNotifications(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleMarkRead = (id) => {
    setNotifications(prev => prev?.map(n => n?.id === id ? { ...n, read_at: new Date()?.toISOString() } : n));
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    await supabase?.from('notifications')?.update({ read_at: new Date()?.toISOString() })?.is('read_at', null);
    setNotifications(prev => prev?.map(n => ({ ...n, read_at: n?.read_at || new Date()?.toISOString() })));
    setMarkingAll(false);
  };

  const filteredNotifs = notifications?.filter(n => {
    if (filterType === 'auto' && n?.type === 'manual') return false;
    if (filterType === 'manual' && n?.type !== 'manual') return false;
    if (filterDest === 'admin' && n?.target_type !== 'admin') return false;
    if (filterDest === 'managers' && n?.target_type !== 'manager') return false;
    if (filterStatus === 'unread' && !!n?.read_at) return false;
    if (filterStatus === 'read' && !n?.read_at) return false;
    if (search) {
      const s = search?.toLowerCase();
      if (!n?.title?.toLowerCase()?.includes(s) && !n?.body?.toLowerCase()?.includes(s)) return false;
    }
    return true;
  });

  const unreadCount = notifications?.filter(n => !n?.read_at)?.length || 0;

  const filterBtnStyle = (active) => ({
    padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontFamily: 'Inter, sans-serif',
    fontWeight: active ? '600' : '400',
    backgroundColor: active ? 'rgba(229,32,42,0.12)' : 'rgba(255,255,255,0.04)',
    color: active ? '#E5202A' : 'rgba(255,255,255,0.5)',
    border: active ? '1px solid rgba(229,32,42,0.25)' : '1px solid transparent',
    cursor: 'pointer', transition: 'all 0.15s',
  });

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} userEmail={userEmail} />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.notificationsPage.title', 'Notifications reçues')} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('admin.notificationsPage.title', 'Notifications reçues')}
              </h1>
              <p className="text-white/40 text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('admin.notificationsPage.subtitle', 'Historique de toutes les notifications.')}
              </p>
            </div>
            {pushStatus !== 'granted' ? (
              <button onClick={handleEnablePush} disabled={pushStatus === 'subscribing' || pushStatus === 'denied'}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all"
                style={{
                  backgroundColor: pushStatus === 'denied' ? '#1f1f1f' : '#E5202A18',
                  color: pushStatus === 'denied' ? 'rgba(255,255,255,0.3)' : '#E5202A',
                  border: `1px solid ${pushStatus === 'denied' ? '#2a2a2a' : '#E5202A30'}`,
                  fontFamily: 'Sora, sans-serif', opacity: pushStatus === 'subscribing' ? 0.6 : 1,
                }}>
                {pushStatus === 'subscribing' ? t('admin.notificationsPage.pushActivating', 'Activation...') : pushStatus === 'denied' ? t('admin.notificationsPage.pushDenied', 'Push refusé') : t('admin.notificationsPage.enablePush', 'Activer push')}
              </button>
            ) : (
              <span className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold"
                style={{ backgroundColor: '#22c55e18', color: '#22c55e', border: '1px solid #22c55e30', fontFamily: 'Sora, sans-serif' }}>
                {t('admin.notificationsPage.pushActive', 'Push actif')}
              </span>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <span className="text-white/30 text-xs mr-1">Type:</span>
                  {[{ key: 'all', label: t('admin.notificationsPage.all', 'All') }, { key: 'auto', label: t('admin.notificationsPage.auto', 'Auto') }, { key: 'manual', label: t('admin.notificationsPage.manual', 'Manual') }].map(f => (
                    <button key={f.key} onClick={() => setFilterType(f.key)} style={filterBtnStyle(filterType === f.key)}>{f.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-white/30 text-xs mr-1">{t('admin.notificationsPage.destLabel', 'Dest:')}</span>
                  {[{ key: 'all', label: t('admin.notificationsPage.all', 'All') }, { key: 'admin', label: t('admin.notificationsPage.admin', 'Admin') }, { key: 'managers', label: t('admin.notificationsPage.managers', 'Managers') }].map(f => (
                    <button key={f.key} onClick={() => setFilterDest(f.key)} style={filterBtnStyle(filterDest === f.key)}>{f.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-white/30 text-xs mr-1">{t('admin.notificationsPage.statusLabel', 'Status:')}</span>
                  {[{ key: 'all', label: t('admin.notificationsPage.all', 'All') }, { key: 'unread', label: t('admin.notificationsPage.unread', 'Unread') }, { key: 'read', label: t('admin.notificationsPage.readStatus', 'Read') }].map(f => (
                    <button key={f.key} onClick={() => setFilterStatus(f.key)} style={filterBtnStyle(filterStatus === f.key)}>{f.label}</button>
                  ))}
                </div>
              </div>
              <button onClick={handleMarkAllRead} disabled={markingAll || unreadCount === 0}
                className="px-4 py-2 rounded-lg text-xs font-medium transition-all"
                style={{ backgroundColor: unreadCount === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)', color: unreadCount === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.08)', cursor: unreadCount === 0 ? 'not-allowed' : 'pointer' }}>
                {markingAll ? t('admin.notificationsPage.markingInProgress', 'In progress...') : t('admin.notificationsPage.markAllRead', 'Mark all as read ({{count}})', { count: unreadCount })}
              </button>
            </div>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" /><path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder={t('admin.notificationsPage.search', 'Rechercher...')}
                className="w-full pl-9 pr-4 py-2.5 rounded-lg text-base text-white outline-none border"
                style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}
                onFocus={e => e.target.style.borderColor = 'var(--color-primary)'} onBlur={e => e.target.style.borderColor = '#1f1f1f'} />
            </div>
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 rounded-full border-2 border-red-500 border-t-transparent animate-spin" />
            </div>
          ) : filteredNotifs?.length === 0 ? (
            <div className="rounded-xl border p-16 flex flex-col items-center gap-3" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
              <p className="text-white/40 text-sm">{t('admin.notificationsPage.noNotification', 'Aucune notification')}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {filteredNotifs?.map(n => <NotificationCard key={n?.id} notification={n} onMarkRead={handleMarkRead} />)}
            </div>
          )}
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default NotificationsReceived;
